import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import { fetchTopMatches, fetchClearFavorites, fetchLiveMatches, fetchUserFavorites, fetchMaradonaHandicap } from 'stores/match/index.js';
import { fetchTopOutrights, selectTopOutrightEvents, selectTopOutrightsLoading } from 'stores/outrights';
import { AWS_BUCKET_URL } from 'toolkit/constants.js';
// components
import BlackBox from 'components/general/black_box.js';
import MatchCard from 'components/general/match_card.js';
import Loading from 'components/general/loading.js';
import useTopTournaments from 'toolkit/useHooks/useTopTournaments';
import { useCasinoPermission } from 'toolkit/useHooks/useCasinoPermission';
import StoriesBar from 'components/stories/StoriesBar';
import TournamentCard from 'views/outrights/components/TournamentCard';

function HomeMobile() {
  const { t } = useTranslation('translations', { useSuspense: false });
  const clearFavorites = useSelector((state) => state.match.clearFavorites);
  const maradonaHandicap = useSelector((state) => state.match.maradonaHandicap);
  const topMatches = useSelector((state) => state.match.topMatches);
  const topOutrights = useSelector(selectTopOutrightEvents);
  const loadingTopOutrights = useSelector(selectTopOutrightsLoading);
  const liveMatches = useSelector((state) => state.match.liveMatches);
  const userFavorites = useSelector((state) => state.match.userFavorites);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const events = useSelector((state) => state.match.events);
  const topTournaments = useTopTournaments(true);
  const { hasCasinoPermission, hasSlotPermission } = useCasinoPermission();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [loadingClearFavorites, setLoadingClearFavorites] = useState(false);
  const [loadingMaradonaHandicap, setLoadingMaradonaHandicap] = useState(false);
  const [loadingTopMatches, setLoadingTopMatches] = useState(false);
  const [loadingLiveMatches, setLoadingLiveMatches] = useState(false);
  const [loadingUserFavorites, setLoadingUserFavorites] = useState(false);
  const swiperOptions = {
    // cssMode: true,
    slidesPerView: 3.5,
    className: 'mb-4',
    loop: true,
    // freeMode: true,
    spaceBetween: 10,
    pagination: {
      clickable: true
    },
    mousewheel: true,
    // autoHeight: true,
    modules: [Mousewheel]
  };

  const getTopCategories = () => {
    const categories = [];

    categories.push({
      to: '/outrights',
      icon:
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'><path d='M6 21V11M12 21V6M18 21v-7' stroke='black' stroke-width='1.8' stroke-linecap='round'/><path d='M4 4l3 3 4-4 4 4 5-5' stroke='black' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' fill='none'/><circle cx='20' cy='2' r='1.6' fill='black'/></svg>",
      text1: t('outrights.label')
    });

    if (user && user.isCasinoEnabled && hasCasinoPermission) {
      categories.push({
        to: '/casino',
        icon: `${AWS_BUCKET_URL}/Icons/liveicon.svg`,
        text1: t('livecasino')
      });
    }

    if (user && user.isCasinoEnabled && hasSlotPermission) {
      categories.push({
        to: '/slot',
        sportTypeVisibleParam: false,
        icon: `${AWS_BUCKET_URL}/Icons/sloticon.svg`,
        text1: t('slot')
      });
    }

    if (!user || !user.isCasinoEnabled || (!hasCasinoPermission && !hasSlotPermission)) {
      categories.push({
        to: '/sport-details?time=Next 4 Hours&show=true',
        icon: `${AWS_BUCKET_URL}/Icons/sandwatchicon.svg`,
        text1: t('pages.sport.next') + ' ' + t('pages.sport.4Hours')
      });
    }

    categories.push(
      {
        to: '/sport-details?time=Today&show=true',
        icon: `${AWS_BUCKET_URL}/Icons/todayicon.svg`,
        text1: t('today')
      },
      {
        to: '/clear-favorites',
        icon: `${AWS_BUCKET_URL}/Icons/clearfavouritesicon.svg`,
        text1: t('clearfavourites')
      },
      {
        to: '/results',
        icon: `${AWS_BUCKET_URL}/Icons/resultsicon.svg`,
        text1: t('results')
      }
    );

    return categories;
  };

  const topCategories = getTopCategories();

  // Filter out invalid matches to prevent empty spaces
  const validUserFavorites = userFavorites.filter(
    (favoriteId) => events[favoriteId] && events[favoriteId].isMatch !== false
  );
  const validClearFavorites = clearFavorites.filter(
    (clearFavoriteId) => events[clearFavoriteId] && events[clearFavoriteId].isMatch !== false
  );

  const validMaradonaHandicap = maradonaHandicap.filter(
    (maradonaHandicapId) => events[maradonaHandicapId] && events[maradonaHandicapId].isMatch !== false
  );

  const validLiveMatches = liveMatches.filter(
    (liveMatchId) => events[liveMatchId] && events[liveMatchId].isMatch !== false
  );

  const getData = () => {
    try {
      setLoadingClearFavorites(true);
      setLoadingMaradonaHandicap(true);
      setLoadingTopMatches(true);
      setLoadingLiveMatches(true);
      setLoadingUserFavorites(true);
      dispatch(fetchTopMatches({ sportType: '0' }))
        .unwrap()
        .then(() => {
          setLoadingTopMatches(false);
        });
      dispatch(fetchTopOutrights());
      dispatch(fetchClearFavorites({ size: 5, sportType: '0' }))
        .unwrap()
        .then(() => {
          setLoadingClearFavorites(false);
        });
      dispatch(fetchMaradonaHandicap({ size: 5, sportType: '0' }))
        .unwrap()
        .then(() => {
          setLoadingMaradonaHandicap(false);
        })
        .catch(() => {
          setLoadingMaradonaHandicap(false);
        });
      dispatch(fetchLiveMatches({ take: 7 }))
        .unwrap()
        .then(() => {
          setLoadingLiveMatches(false);
        });
      dispatch(fetchUserFavorites())
        .unwrap()
        .then(() => {
          setLoadingUserFavorites(false);
        })
        .catch(() => {
          setLoadingUserFavorites(false);
        });
    } catch (error) {
      // error
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getData();
  }, []);

  return (
    <Container fluid className="g-1">
      {/* top matches start */}
      {loadingTopMatches ? (
        <Loading message={t('pages.start.messageLoadingTopMatches')} />
      ) : (
        <section className="my-3">
          <div className="d-flex justify-content-between">
            <h6>{t('pages.start.titleTopMatches')}</h6>
            <Link className="link-primary fs-7" to="/top-matches">
              {t('pages.start.linkTopMatches')}
            </Link>
          </div>
          <Swiper {...{ ...swiperOptions, slidesPerView: 1.1, className: '' }}>
            {topOutrights.map((event) => (
              <SwiperSlide
                key={event.se?.id}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <TournamentCard event={event} marketCount={1} fillHeight hideMoreOutcomes />
              </SwiperSlide>
            ))}
            {topMatches.map((topMatch) => {
              return (
                <SwiperSlide key={topMatch}>
                  <MatchCard
                    providerId={topMatch}
                    marketType={1}
                    locales={locales}
                    selectedLocale={selectedLocale}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </section>
      )}
      {/* top matches end */}

      {/* stories start */}
      <StoriesBar />
      {/* stories end */}

      {/* top categories start */}
      <section className="mb-3">
        <h6>{t('pages.start.titleTopCategories')}</h6>
        <div className="d-flex gap-2 overflow-x-auto">
          {topCategories.map((tc, i) => (
            <div key={i} style={{ width: '115px', flexShrink: 0 }}>
              <BlackBox {...tc} />
            </div>
          ))}
        </div>
      </section>
      {/* top categories end */}

      {/* top leagues start */}
      <section className="mb-3">
        <h6>{t('pages.start.titleTopLeagues')}</h6>

        <div className="d-flex gap-2 overflow-x-auto">
          {topTournaments.map((topTournamentArr) => {
            const [topTournament1, topTournament2] = topTournamentArr;
            return (
              <div key={topTournament1.id} style={{ width: '115px', flexShrink: 0 }}>
                <BlackBox
                  to={topTournament1?.link}
                  text1={topTournament1?.text}
                  text2={''}
                  lineClamp={true}
                  iconType="flag"
                  icon={`${AWS_BUCKET_URL}/ClientFlags/${topTournament1.categoryId.split('sr:category:')[1]
                    }.svg`}
                />
                <div className="my-2"></div>
                <BlackBox
                  to={topTournament2?.link}
                  text1={topTournament2?.text}
                  text2={''}
                  lineClamp={true}
                  iconType="flag"
                  icon={`${AWS_BUCKET_URL}/ClientFlags/${topTournament2?.categoryId?.split('sr:category:')[1]
                    }.svg`}
                />
              </div>
            );
          })}
        </div>
      </section>
      {/* top leagues end */}

      {/* live matches start */}
      {loadingLiveMatches ? (
        <Loading message={t('pages.start.messageLoadingLiveMatches')} />
      ) : (
        <section className="my-3">
          <div className="d-flex justify-content-between">
            <h6>{t('pages.start.titleLiveMatches')}</h6>
            <Link className="link-primary fs-7" to="/live">
              {t('pages.start.linkLiveMatches')}
            </Link>
          </div>
          <Swiper {...{ ...swiperOptions, slidesPerView: 1.1, className: '' }}>
            {validLiveMatches.map((liveMatchId) => {
              return (
                <SwiperSlide key={liveMatchId}>
                  <MatchCard
                    providerId={liveMatchId}
                    marketType={1}
                    locales={locales}
                    selectedLocale={selectedLocale}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </section>
      )}
      {/* live matches end */}

      {/* user favorites start */}
      {loadingUserFavorites ? (
        <Loading message={t('pages.start.messageLoadingFavorites')} />
      ) : validUserFavorites.length > 0 ? (
        <section className="my-3">
          <div className="d-flex justify-content-between">
            <h6>{t('myFavorites')}</h6>
            <Link className="link-primary fs-7" to="/favorites">
              {t('pages.start.viewAll')}
            </Link>
          </div>
          <Swiper {...{ ...swiperOptions, slidesPerView: 1.1, className: '' }}>
            {validUserFavorites.map((favoriteId) => {
              return (
                <SwiperSlide key={favoriteId}>
                  <MatchCard
                    providerId={favoriteId}
                    marketType={1}
                    locales={locales}
                    selectedLocale={selectedLocale}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </section>
      ) : null}
      {/* user favorites end */}

      {loadingClearFavorites ? (
        <Loading message={t('pages.start.messageLoadingClearFavorites')} />
      ) : (
        <section className="mb-3">
          <div className="d-flex justify-content-between">
            <h6>{t('pages.start.titleClearFavorites')}</h6>
            <Link className="link-primary fs-7" to="/clear-favorites">
              {t('pages.start.linkClearFavorites')}
            </Link>
          </div>
          <Swiper {...swiperOptions} slidesPerView={1.1}>
            {validClearFavorites.slice(0, 5).map((clearFavoritesMatchId) => (
              <SwiperSlide key={clearFavoritesMatchId}>
                <MatchCard
                  providerId={clearFavoritesMatchId}
                  marketType={1}
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </section>
      )}

      {loadingMaradonaHandicap ? (
        <Loading message={t('pages.start.messageLoadingMaradonaHandicap')} />
      ) : validMaradonaHandicap.length > 0 ? (
        <section className="mb-3">
          <div className="d-flex justify-content-between">
            <h6>{t('pages.start.titleMaradonaHandicap')}</h6>
            <Link className="link-primary fs-7" to="/maradona-handicap">
              {t('pages.start.linkMaradonaHandicap')}
            </Link>
          </div>
          <Swiper {...swiperOptions} slidesPerView={1.1}>
            {validMaradonaHandicap.slice(0, 5).map((maradonaHandicapMatchId) => (
              <SwiperSlide key={maradonaHandicapMatchId}>
                <MatchCard
                  providerId={maradonaHandicapMatchId}
                  marketType={14}
                  isMaradonaHandicap
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </section>
      ) : null}
    </Container>
  );
}
export default HomeMobile;
